import { logger } from "@/lib/logger";
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type UserContext = 'professional' | 'site';

const ACTIVE_CONTEXT_KEY = 'active_user_context';
const CONTEXT_TIMESTAMP_KEY = 'active_context_timestamp';
const CONTEXT_LOCK_DURATION = 2000; // 2 seconds to prevent rapid switching

interface UseUserContextsReturn {
  contexts: UserContext[];
  hasProfessionalAccess: boolean;
  hasSiteAccess: boolean;
  activeContext: UserContext | null;
  setActiveContext: (context: UserContext) => void;
  loading: boolean;
}

export function useUserContexts(): UseUserContextsReturn {
  const { user, userProfile, isAuthReady } = useAuth();
  const [contexts, setContexts] = useState<UserContext[]>([]);
  const [activeContext, setActiveContextState] = useState<UserContext | null>(null);
  const [loading, setLoading] = useState(true);

  const setActiveContext = (context: UserContext) => {
    const now = Date.now();
    const lastSwitch = localStorage.getItem(CONTEXT_TIMESTAMP_KEY);
    
    // Prevent rapid context switching
    if (lastSwitch && (now - parseInt(lastSwitch)) < CONTEXT_LOCK_DURATION) {
      logger.debug("⏱️ Context switch too soon, ignoring");
      return;
    }
    
    logger.debug("🔄 Context switching to:", context);
    setActiveContextState(context);
    localStorage.setItem(ACTIVE_CONTEXT_KEY, context);
    localStorage.setItem(CONTEXT_TIMESTAMP_KEY, now.toString());
  };

  useEffect(() => {
    async function detectContexts() {
      if (!isAuthReady || !user?.id) {
        setLoading(false);
        return;
      }

      try {
        const userRole = userProfile?.role?.toLowerCase();
        
        // Check if user has professional access based on role
        const hasProfessionalAccess = [
          'medical_professional', 
          'healthcare_provider'
        ].includes(userRole || '');
        
        // Check if user has site access based on role OR organization membership
        let hasSiteAccess = userRole === 'trial_site';
        
        if (!hasSiteAccess) {
          // Check if user is member of any site organizations
          const { data: memberships } = await supabase
            .from('organization_members')
            .select('organizations!inner(type)')
            .eq('user_id', user.id);
          
          hasSiteAccess = memberships?.some((m: any) => 
            m.organizations?.type === 'site'
          ) || false;
        }
        
        // Build contexts array
        const availableContexts: UserContext[] = [];
        if (hasProfessionalAccess) availableContexts.push('professional');
        if (hasSiteAccess) availableContexts.push('site');
        
        setContexts(availableContexts);
        
        // Set active context
        if (availableContexts.length > 0) {
          const storedContext = localStorage.getItem(ACTIVE_CONTEXT_KEY) as UserContext;
          
          if (storedContext && availableContexts.includes(storedContext)) {
            setActiveContextState(storedContext);
          } else {
            // Default based on role priority
            const defaultContext = hasProfessionalAccess ? 'professional' : 'site';
            setActiveContextState(defaultContext);
            localStorage.setItem(ACTIVE_CONTEXT_KEY, defaultContext);
          }
        }
      } catch (error) {
        logger.error('Error detecting user contexts:', error);
      } finally {
        setLoading(false);
      }
    }

    detectContexts();
  }, [user?.id, userProfile?.role, isAuthReady]);

  return {
    contexts,
    hasProfessionalAccess: contexts.includes('professional'),
    hasSiteAccess: contexts.includes('site'),
    activeContext,
    setActiveContext,
    loading
  };
}
